import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Users, Clock, Home, Layers, TrendingUp, Eye, MousePointer, DollarSign } from 'lucide-react';
const engagementData = [{
  name: 'Mon',
  sessions: 40,
  time: 240,
  buyers: 12
}, {
  name: 'Tue',
  sessions: 30,
  time: 139,
  buyers: 8
}, {
  name: 'Wed',
  sessions: 20,
  time: 980,
  buyers: 15
}, {
  name: 'Thu',
  sessions: 27,
  time: 390,
  buyers: 10
}, {
  name: 'Fri',
  sessions: 18,
  time: 480,
  buyers: 14
}, {
  name: 'Sat',
  sessions: 23,
  time: 380,
  buyers: 9
}, {
  name: 'Sun',
  sessions: 34,
  time: 430,
  buyers: 11
}];
const modelTimeData = [{
  name: 'The Lawson',
  time: 2840,
  buyers: 45,
  avgTime: 63,
  upgradeValue: 142500
}, {
  name: 'The Samson',
  time: 1920,
  buyers: 32,
  avgTime: 60,
  upgradeValue: 128800
}, {
  name: 'The Pine',
  time: 1680,
  buyers: 28,
  avgTime: 60,
  upgradeValue: 0
}, {
  name: 'The Cedar',
  time: 1140,
  buyers: 19,
  avgTime: 60,
  upgradeValue: 0
}, {
  name: 'The Birch',
  time: 720,
  buyers: 12,
  avgTime: 60,
  upgradeValue: 0
}];
const optionsData = [{
  name: 'Quartz Countertops',
  count: 85,
  percentage: 68,
  revenue: 42500
}, {
  name: 'Hardwood Flooring',
  count: 72,
  percentage: 58,
  revenue: 51840
}, {
  name: 'Smart Home Package',
  count: 65,
  percentage: 52,
  revenue: 58500
}, {
  name: 'Finished Basement',
  count: 45,
  percentage: 36,
  revenue: 202500
}, {
  name: 'Deck Extension',
  count: 38,
  percentage: 30,
  revenue: 23560
}];
const elevationData = [{
  name: 'Modern',
  value: 42,
  color: '#06b6d4'
}, {
  name: 'Traditional',
  value: 28,
  color: '#334155'
}, {
  name: 'Craftsman',
  value: 18,
  color: '#64748b'
}, {
  name: 'Contemporary',
  value: 12,
  color: '#94a3b8'
}];
const floorplanData = [{
  name: 'Open Concept',
  value: 65
}, {
  name: 'Traditional',
  value: 25
}, {
  name: 'Split Level',
  value: 10
}];
const colorPaletteData = [{
  name: 'Modern Neutral',
  count: 48,
  color: '#e2e8f0'
}, {
  name: 'Warm Earth',
  count: 32,
  color: '#d4a574'
}, {
  name: 'Cool Gray',
  count: 28,
  color: '#94a3b8'
}, {
  name: 'Classic White',
  count: 16,
  color: '#f8fafc'
}];
const upgradeRevenueData = [{
  name: 'Week 1',
  revenue: 45200
}, {
  name: 'Week 2',
  revenue: 52800
}, {
  name: 'Week 3',
  revenue: 48500
}, {
  name: 'Week 4',
  revenue: 61300
}, {
  name: 'Week 5',
  revenue: 58400
}, {
  name: 'Week 6',
  revenue: 72100
}, {
  name: 'Week 7',
  revenue: 68500
}];
const projects = ['All Projects', 'The Lawson', 'The Samson', 'The Pine', 'The Cedar', 'The Birch'];
export function AnalyticsPage() {
  const [selectedProject, setSelectedProject] = useState('All Projects');
  const [timeRange, setTimeRange] = useState('7d');
  // Upgrade stats
  const upgradeStats = {
    totalRevenue: 271300,
    avgPerCustomer: 45217,
    topCategory: 'Kitchen',
    conversionRate: 68
  };
  return <div className="space-y-8 max-w-7xl mx-auto" data-id="element-1134">
      {/* Header with Filters */}
      <div className="flex items-center justify-between" data-id="element-1135">
        <div data-id="element-1136">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1137">Analytics</h1>
          <p className="text-slate-500" data-id="element-1138">
            Deep insights into buyer behavior and upgrade revenue
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1139">
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1140">
            {projects.map(project => <option key={project} value={project} data-id="element-1141">
                {project}
              </option>)}
          </select>
          <select value={timeRange} onChange={e => setTimeRange(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1142">
            <option value="7d" data-id="element-1143">Last 7 Days</option>
            <option value="30d" data-id="element-1144">Last 30 Days</option>
            <option value="90d" data-id="element-1145">Last 90 Days</option>
          </select>
          <Button variant="outline" data-id="element-1146">Export Report</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1147">
        <Card data-id="element-1148">
          <CardContent className="p-6" data-id="element-1149">
            <div className="flex items-center gap-4" data-id="element-1150">
              <div className="p-3 bg-emerald-50 rounded-lg flex-shrink-0" data-id="element-1151">
                <DollarSign className="h-6 w-6 text-emerald-600" data-id="element-1152" />
              </div>
              <div data-id="element-1153">
                <p className="text-sm font-medium text-slate-500" data-id="element-1154">
                  Total Upgrade Revenue
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-1155">
                  ${upgradeStats.totalRevenue.toLocaleString()}
                </h3>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-1156">
                  <TrendingUp className="h-3 w-3 mr-1" data-id="element-1157" />
                  <span className="font-medium" data-id="element-1158">+18%</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1159">
          <CardContent className="p-6" data-id="element-1160">
            <div className="flex items-center gap-4" data-id="element-1161">
              <div className="p-3 bg-purple-50 rounded-lg flex-shrink-0" data-id="element-1162">
                <TrendingUp className="h-6 w-6 text-purple-600" data-id="element-1163" />
              </div>
              <div data-id="element-1164">
                <p className="text-sm font-medium text-slate-500" data-id="element-1165">
                  Avg Upgrade Value
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-1166">
                  ${upgradeStats.avgPerCustomer.toLocaleString()}
                </h3>
                <p className="text-xs text-slate-500 mt-1" data-id="element-1167">Per customer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1168">
          <CardContent className="p-6" data-id="element-1169">
            <div className="flex items-center gap-4" data-id="element-1170">
              <div className="p-3 bg-primary-50 rounded-lg flex-shrink-0" data-id="element-1171">
                <Users className="h-6 w-6 text-primary-600" data-id="element-1172" />
              </div>
              <div data-id="element-1173">
                <p className="text-sm font-medium text-slate-500" data-id="element-1174">
                  Total Buyers
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-1175">124</h3>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-1176">
                  <TrendingUp className="h-3 w-3 mr-1" data-id="element-1177" />
                  <span className="font-medium" data-id="element-1178">+12%</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1179">
          <CardContent className="p-6" data-id="element-1180">
            <div className="flex items-center gap-4" data-id="element-1181">
              <div className="p-3 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-1182">
                <MousePointer className="h-6 w-6 text-blue-600" data-id="element-1183" />
              </div>
              <div data-id="element-1184">
                <p className="text-sm font-medium text-slate-500" data-id="element-1185">
                  Selection Rate
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-1186">
                  {upgradeStats.conversionRate}%
                </h3>
                <p className="text-xs text-slate-500 mt-1" data-id="element-1187">
                  Buyers with upgrades
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upgrade Revenue Chart */}
      <Card data-id="element-1188">
        <CardHeader data-id="element-1189">
          <CardTitle data-id="element-1190">Upgrade Revenue Over Time</CardTitle>
        </CardHeader>
        <CardContent data-id="element-1191">
          <div className="h-[300px] w-full" data-id="element-1192">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1193">
              <AreaChart data={upgradeRevenueData} data-id="element-1194">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-1195" />
                <XAxis dataKey="name" tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-1196" />
                <YAxis tick={{
                fill: '#64748b',
                fontSize: 12
              }} tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-1197" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']} data-id="element-1198" />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="#d1fae5" strokeWidth={2} data-id="element-1199" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1200">
        <Card data-id="element-1201">
          <CardHeader data-id="element-1202">
            <CardTitle data-id="element-1203">Engagement Over Time</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1204">
            <div className="h-[300px] w-full" data-id="element-1205">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1206">
                <LineChart data={engagementData} data-id="element-1207">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-1208" />
                  <XAxis dataKey="name" tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-1209" />
                  <YAxis tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-1210" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-1211" />
                  <Legend data-id="element-1212" />
                  <Line type="monotone" dataKey="sessions" stroke="#06b6d4" strokeWidth={2} name="Sessions" data-id="element-1213" />
                  <Line type="monotone" dataKey="buyers" stroke="#334155" strokeWidth={2} name="New Buyers" data-id="element-1214" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1215">
          <CardHeader data-id="element-1216">
            <CardTitle data-id="element-1217">Model Engagement</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1218">
            <div className="h-[300px] w-full" data-id="element-1219">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1220">
                <BarChart data={modelTimeData} layout="vertical" data-id="element-1221">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" data-id="element-1222" />
                  <XAxis type="number" hide data-id="element-1223" />
                  <YAxis dataKey="name" type="category" width={100} tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} axisLine={false} tickLine={false} data-id="element-1224" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-1225" />
                  <Bar dataKey="time" fill="#06b6d4" radius={[0, 4, 4, 0]} barSize={24} name="Total Time (mins)" data-id="element-1226" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Options and Revenue Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1227">
        <Card data-id="element-1228">
          <CardHeader data-id="element-1229">
            <CardTitle data-id="element-1230">Top Selected Options by Revenue</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1231">
            <div className="space-y-4" data-id="element-1232">
              {optionsData.map((option, index) => <div key={option.name} data-id="element-1233">
                  <div className="flex items-center justify-between mb-2" data-id="element-1234">
                    <div className="flex items-center gap-3" data-id="element-1235">
                      <span className="text-sm font-medium text-slate-700" data-id="element-1236">
                        {index + 1}
                      </span>
                      <span className="text-sm text-slate-900" data-id="element-1237">
                        {option.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-1238">
                      <span className="text-sm font-bold text-emerald-600" data-id="element-1239">
                        ${option.revenue.toLocaleString()}
                      </span>
                      <Badge variant="neutral" data-id="element-1240">{option.percentage}%</Badge>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-1241">
                    <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{
                  width: `${option.percentage}%`
                }} data-id="element-1242" />
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1243">
          <CardHeader data-id="element-1244">
            <CardTitle data-id="element-1245">Elevation Preferences</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1246">
            <div className="h-[280px] w-full flex items-center justify-center" data-id="element-1247">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1248">
                <PieChart data-id="element-1249">
                  <Pie data={elevationData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" data-id="element-1250">
                    {elevationData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1251" />)}
                  </Pie>
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-1252" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4" data-id="element-1253">
              {elevationData.map(item => <div key={item.name} className="flex items-center gap-2" data-id="element-1254">
                  <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: item.color
              }} data-id="element-1255" />
                  <span className="text-sm text-slate-600" data-id="element-1256">{item.name}</span>
                  <span className="text-sm font-medium text-slate-900 ml-auto" data-id="element-1257">
                    {item.value}%
                  </span>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Insights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1258">
        <Card data-id="element-1259">
          <CardHeader data-id="element-1260">
            <CardTitle data-id="element-1261">Floor Plan Preferences</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1262">
            <div className="h-[240px] w-full" data-id="element-1263">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1264">
                <BarChart data={floorplanData} data-id="element-1265">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-1266" />
                  <XAxis dataKey="name" tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-1267" />
                  <YAxis tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-1268" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-1269" />
                  <Bar dataKey="value" fill="#334155" radius={[4, 4, 0, 0]} barSize={60} data-id="element-1270" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1271">
          <CardHeader data-id="element-1272">
            <CardTitle data-id="element-1273">Color Palette Selections</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1274">
            <div className="space-y-4" data-id="element-1275">
              {colorPaletteData.map(palette => <div key={palette.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-1276">
                  <div className="flex items-center gap-3" data-id="element-1277">
                    <div className="w-10 h-10 rounded-md border-2 border-slate-200" style={{
                  backgroundColor: palette.color
                }} data-id="element-1278" />
                    <span className="text-sm font-medium text-slate-900" data-id="element-1279">
                      {palette.name}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900" data-id="element-1280">
                    {palette.count}
                  </span>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}